/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Minus, Plus, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ordersApi, addressesApi, paymentsApi, couponsApi } from '../lib/api';
import { loadRazorpayCheckout } from '../lib/razorpay';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stage, setStage] = useState('cart'); // cart | checkout | success
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [lastOrder, setLastOrder] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);

  // Load saved addresses and preselect the default whenever entering checkout
  useEffect(() => {
    if (stage !== 'checkout' || !user) return;
    (async () => {
      try {
        const addrs = await addressesApi.list();
        setAddresses(addrs);
        if (addrs.length > 0) {
          const def = addrs.find((a) => a.is_default) || addrs[0];
          setSelectedAddressId(def.id);
        } else {
          setSelectedAddressId(null);
        }
      } catch (e) {
        setAddresses([]);
        setSelectedAddressId(null);
      }
    })();
  }, [stage, user]);

  if (!isOpen) return null;

  const close = () => {
    setIsOpen(false);
    setTimeout(() => { setStage('cart'); setErr(''); }, 200);
  };

  const startCheckout = () => {
    if (!user) { setIsOpen(false); navigate('/login'); return; }
    setStage('checkout');
  };

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponErr(''); setCouponBusy(true);
    try {
      const res = await couponsApi.apply({ code, payment_method: 'online' });
      setAppliedCoupon(res);
    } catch (e) {
      setAppliedCoupon(null);
      setCouponErr(e?.response?.data?.detail || 'Could not apply coupon');
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponErr('');
  };

  const submitOrder = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const couponCode = appliedCoupon?.code || null;
      const addressPayload = { address_id: selectedAddressId, coupon_code: couponCode };

      // 1) Create Razorpay order via backend (discount applied server-side)
      const rzp = await paymentsApi.createOrder(addressPayload);
      // 2) Load Razorpay checkout script
      const Razorpay = await loadRazorpayCheckout();
      // 3) Open payment modal
      const options = {
        key: rzp.key_id,
        amount: rzp.amount,
        currency: rzp.currency || 'INR',
        name: 'VELOUVA',
        description: 'Order payment',
        order_id: rzp.razorpay_order_id,
        prefill: {
          name: addresses.find((a) => a.id === selectedAddressId)?.full_name || user?.name || '',
          email: user?.email || '',
        },
        theme: { color: '#1a1410' },
        modal: {
          ondismiss: () => {
            setBusy(false);
            setErr('Payment cancelled.');
          },
        },
        handler: async (response) => {
          try {
            const verified = await paymentsApi.verify({
              ...addressPayload,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            await clearCart();
            setLastOrder(verified);
            setStage('success');
          } catch (verifyErr) {
            setErr(verifyErr?.response?.data?.detail || 'Payment verification failed. Please contact support.');
          } finally {
            setBusy(false);
          }
        },
      };
      const rzpInstance = new Razorpay(options);
      rzpInstance.on('payment.failed', (resp) => {
        setBusy(false);
        setErr(resp?.error?.description || 'Payment failed. Please try again.');
      });
      rzpInstance.open();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || 'Checkout failed. Please try again.');
      setBusy(false);
    }
  };

  const baseShipping = subtotal >= 75 ? 0 : 8;
  const discount = appliedCoupon ? appliedCoupon.discount : 0;
  const total = appliedCoupon ? appliedCoupon.total : subtotal + baseShipping;
  const canPlace = !!selectedAddressId;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={close} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-[#f5f1e6] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-6 h-20 border-b border-[#e5dfd0]">
          <div className="text-[11px] tracking-[0.28em] text-[#3a2e26]">
            {stage === 'cart' && 'YOUR BAG'}
            {stage === 'checkout' && 'CHECKOUT'}
            {stage === 'success' && 'ORDER CONFIRMED'}
          </div>
          <button onClick={close} aria-label="Close">
            <X className="w-5 h-5" strokeWidth={1.4} />
          </button>
        </div>

        {stage === 'cart' && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <div className="text-center mt-16">
                  <p className="font-serif text-2xl text-[#1a1410]">Your bag is empty.</p>
                  <p className="mt-2 text-sm text-[#7a6a5a]">A shade is waiting to be composed.</p>
                  <button onClick={() => { close(); navigate('/shop'); }} className="mt-8 inline-block text-[11px] tracking-[0.28em] border-b border-[#1a1410] pb-1">
                    BROWSE THE MAISON
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item) => (
                    <div key={item.id} className="flex gap-4 pb-6 border-b border-[#e5dfd0]">
                      <img src={item.image} alt={item.name} className="w-20 h-24 object-cover" />
                      <div className="flex-1 flex flex-col">
                        <div className="text-[10px] tracking-[0.22em] uppercase text-[#7a6a5a]">{item.category}</div>
                        <div className="font-serif text-lg text-[#1a1410] leading-tight mt-1">{item.name}</div>
                        <div className="text-xs text-[#7a6a5a] mt-0.5">Shade — {item.shade}</div>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center border border-[#cdc2b1]">
                            <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="px-2 py-1"><Minus className="w-3 h-3" strokeWidth={1.4} /></button>
                            <span className="px-3 text-sm">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="px-2 py-1"><Plus className="w-3 h-3" strokeWidth={1.4} /></button>
                          </div>
                          <div className="font-medium text-[#1a1410] tabular-nums">Rs. {(item.price * item.quantity).toFixed(2)}</div>
                        </div>
                        <button onClick={() => removeItem(item.id)} className="text-[10px] tracking-[0.22em] text-[#7a6a5a] hover:text-[#1a1410] mt-2 self-start">REMOVE</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#e5dfd0] px-6 py-6">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-[11px] tracking-[0.28em] text-[#3a2e26]">SUBTOTAL</span>
                  <span className="font-medium text-lg text-[#1a1410] tabular-nums">Rs. {subtotal.toFixed(2)}</span>
                </div>
                <button onClick={startCheckout} className="w-full bg-[#1a1410] text-[#f5f1e6] py-4 text-[11px] tracking-[0.32em] hover:bg-black transition-colors">
                  {user ? 'CHECKOUT' : 'SIGN IN TO CHECKOUT'}
                </button>
              </div>
            )}
          </>
        )}

        {stage === 'checkout' && (
          <form onSubmit={submitOrder} className="flex-1 flex flex-col">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
              {/* Saved addresses */}
              {addresses.length > 0 ? (
                <div className="space-y-3">
                  <div className="text-[11px] tracking-[0.28em] text-[#3a2e26]">CHOOSE AN ADDRESS</div>
                  {addresses.map((a) => {
                    const active = selectedAddressId === a.id;
                    return (
                      <button
                        type="button"
                        key={a.id}
                        data-testid={`checkout-address-${a.id}`}
                        onClick={() => setSelectedAddressId(a.id)}
                        className={`w-full text-left border p-4 flex items-start gap-3 transition-colors ${
                          active ? 'border-[#1a1410] bg-[#ece4d2]' : 'border-[#cdc2b1] hover:border-[#1a1410]'
                        }`}
                      >
                        <span className={`mt-1 w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${active ? 'border-[#1a1410] bg-[#1a1410]' : 'border-[#7a6a5a]'}`}>
                          {active && <Check className="w-2.5 h-2.5 text-[#f5f1e6]" strokeWidth={3} />}
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] tracking-[0.22em] uppercase text-[#7a6a5a]">{a.label || 'Home'}</span>
                            {a.is_default && <span className="text-[9px] tracking-[0.22em] bg-[#1a1410] text-[#f5f1e6] px-1.5 py-0.5">DEFAULT</span>}
                          </div>
                          <div className="font-serif text-base mt-1">{a.full_name}</div>
                          <div className="text-xs text-[#3a2e26] mt-1 leading-snug">
                            {a.address}, {a.city}, {a.postal_code}, {a.country}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="border border-[#cdc2b1] p-6 text-center" data-testid="checkout-no-address">
                  <p className="font-serif text-lg">No saved address yet.</p>
                  <p className="mt-2 text-sm text-[#5a4a3e]">
                    Add a delivery address in your account first, then come back to complete your order.
                  </p>
                  <button
                    type="button"
                    onClick={() => { close(); navigate('/account'); }}
                    className="mt-6 inline-block bg-[#1a1410] text-[#f5f1e6] px-8 py-3 text-[11px] tracking-[0.32em] hover:bg-black transition-colors"
                  >
                    GO TO MY ACCOUNT
                  </button>
                </div>
              )}

              {err && <div className="p-3 bg-[#f3e3d8] border border-[#cdc2b1] text-sm text-[#7a3a2a]">{err}</div>}

              {/* Coupon */}
              <div className="pt-4 border-t border-[#e5dfd0] mt-6" data-testid="coupon-section">
                {!appliedCoupon ? (
                  <div>
                    <div className="flex gap-2">
                      <input
                        value={couponInput}
                        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                        placeholder="COUPON CODE"
                        data-testid="coupon-input"
                        className="flex-1 bg-white border border-[#cdc2b1] px-3 py-2 text-sm font-mono tracking-wide focus:outline-none focus:border-[#1a1410]"
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponBusy || !couponInput.trim()}
                        data-testid="apply-coupon-btn"
                        className="px-5 bg-[#1a1410] text-[#f5f1e6] text-[11px] tracking-[0.28em] hover:bg-black disabled:opacity-50"
                      >
                        {couponBusy ? '…' : 'APPLY'}
                      </button>
                    </div>
                    {couponErr && <div className="mt-2 text-xs text-[#7a3a2a]" data-testid="coupon-error">{couponErr}</div>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-[#e6efe1] border border-[#b7ceac] px-3 py-2" data-testid="coupon-applied">
                    <div className="text-sm">
                      <span className="font-mono font-medium">{appliedCoupon.code}</span>
                      <span className="text-[#3d5a2a]"> applied · − Rs. {appliedCoupon.discount.toFixed(2)}</span>
                    </div>
                    <button type="button" onClick={removeCoupon} data-testid="remove-coupon-btn" className="text-[11px] tracking-[0.2em] text-[#7a3a2a] hover:text-[#5a1a0a]">REMOVE</button>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-[#e5dfd0] mt-6 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-[#7a6a5a]">Subtotal</span><span>Rs. {subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#7a6a5a]">Shipping</span><span>{baseShipping === 0 ? 'Free' : 'Rs. 8.00'}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-[#3d5a2a]" data-testid="discount-line"><span>Discount</span><span>− Rs. {discount.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between font-medium text-base pt-2 tabular-nums" data-testid="checkout-total"><span>Total</span><span>Rs. {total.toFixed(2)}</span></div>
              </div>
            </div>
            <div className="border-t border-[#e5dfd0] px-6 py-6 space-y-3">
              <button type="submit" disabled={busy || !canPlace} data-testid="pay-now-btn" className="w-full bg-[#1a1410] text-[#f5f1e6] py-4 text-[11px] tracking-[0.32em] hover:bg-black transition-colors disabled:opacity-50">
                {busy ? 'OPENING PAYMENT…' : `PAY Rs. ${total.toFixed(2)}`}
              </button>
              <button type="button" onClick={() => setStage('cart')} className="w-full text-[11px] tracking-[0.28em] text-[#7a6a5a] hover:text-[#1a1410]">
                ← BACK TO BAG
              </button>
            </div>
          </form>
        )}

        {stage === 'success' && lastOrder && (
          <div className="flex-1 px-6 py-12 text-center">
            <div className="text-[11px] tracking-[0.28em] text-[#3a2e26] border-t border-[#1a1410] pt-3 inline-block">CONFIRMED</div>
            <h2 className="font-serif text-3xl italic mt-6">Thank you.</h2>
            <p className="mt-3 text-sm text-[#5a4a3e]">Order {lastOrder.id.slice(0, 8).toUpperCase()} is composed.</p>
            <p className="mt-1 font-medium text-lg tabular-nums">Rs. {lastOrder.total.toFixed(2)}</p>
            <button onClick={() => { close(); navigate('/account'); }} className="mt-10 inline-block bg-[#1a1410] text-[#f5f1e6] px-8 py-4 text-[11px] tracking-[0.32em]">
              VIEW ORDERS
            </button>
            <button onClick={() => { close(); navigate('/shop'); }} className="mt-4 block w-full text-[11px] tracking-[0.28em] text-[#7a6a5a]">
              CONTINUE SHOPPING
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
